import {match} from 'ts-pattern';

import {UseFormReturn, useFormState} from 'react-hook-form';

import {always} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {VehicleTypeEnumObject} from '@dms/api/shared';

import {createVehicleTitle} from 'features/vehicle-catalogue';

import {ServiceVehicleFormType} from '../../../types/ServiceVehicleFormType';
import {useVehicleMake} from './useVehicleMake';
import {useVehicleModel} from './useVehicleModel';

interface UseSetVehicleTitleIfAutoGeneratedProps {
  formApi: UseFormReturn<ServiceVehicleFormType>;
  vehicleType: VehicleTypeEnumObject;
}

/**
 * Writes a generated title unless the user owns the current one.
 *
 * A title the user typed - in this session or an earlier one - has to survive a VIN decode and a
 * make/model/variant change. vehicle-forms-kit's TitleField makes the same call (T20-94647); this
 * form predates it and overwrote the title unconditionally.
 *
 * The returned setter reads the form at call time, because the VIN decode awaits two catalogue
 * requests before writing and the user can type a title in that window.
 */
export function useSetVehicleTitleIfAutoGenerated(props: UseSetVehicleTitleIfAutoGeneratedProps) {
  const {defaultValues} = useFormState({control: props.formApi.control, name: 'title'});

  const {unknownMakes, unknownModels} = props.formApi.getValues();

  const {
    detail: {flatMakes},
  } = useVehicleMake({vehicleType: props.vehicleType, unknownMakes});

  const {
    detail: {flatModels},
  } = useVehicleModel({
    vehicleType: props.vehicleType,
    make: defaultValues?.formMake,
    unknownModels,
  });

  return (newTitle: string | null) => {
    const loadedTitle = defaultValues?.title;

    const shouldOverwrite = match({
      isCurrentTitleEmpty: isNilOrEmpty(props.formApi.getValues('title')),
      isTitleDirty: props.formApi.getFieldState('title').isDirty,
      isLoadedTitleEmpty: isNilOrEmpty(loadedTitle),
    })
      // An emptied title is not a custom one, so it is refilled rather than left blank - the field
      // is neither required nor validated, so keeping it would let a blank title be saved.
      .with({isCurrentTitleEmpty: true}, always(true))
      .with({isTitleDirty: true}, always(false))
      .with({isLoadedTitleEmpty: true}, always(true))
      // While the catalogue loads, the lookups fall back to raw codes and no loaded title can
      // match, so the title is kept rather than overwritten with a code.
      .otherwise(
        () =>
          loadedTitle ===
          createVehicleTitle({
            flatMakes,
            flatModels,
            formMake: defaultValues?.formMake,
            formModel: defaultValues?.formModelFamily,
            variant: defaultValues?.variant,
          })
      );

    if (shouldOverwrite) {
      props.formApi.setValue('title', newTitle);
    }
  };
}
